import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionButton from '../../components/ui/QuickActionButton';
import UserStatsCards from './components/UserStatsCards';
import UserFilters from './components/UserFilters';
import BulkActionsBar from './components/BulkActionsBar';
import UserTable from './components/UserTable';
import UserModal from './components/UserModal';
import ConfirmationModal from './components/ConfirmationModal';
import Pagination from './components/Pagination';

const UserAdministration = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: ''
  });
  const [sortConfig, setSortConfig] = useState({
    key: 'name',
    direction: 'asc'
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 25
  });
  const [modals, setModals] = useState({
    userModal: { isOpen: false, user: null, mode: 'create' },
    confirmModal: { isOpen: false, type: 'warning', title: '', message: '', action: null }
  });
  const [isLoading, setIsLoading] = useState(false);

  // Mock user data
  const mockUsers = [
    {
      id: 1,
      name: "Carlos Rodríguez Hernández",
      email: "carlos.rodriguez@vinpro.mx",
      role: "admin",
      status: "active",
      dailyLimit: 50,
      dailyQueriesUsed: 23,
      twoFactorEnabled: true,
      lastLogin: "2024-10-17T19:30:00Z",
      createdAt: "2024-01-15T10:00:00Z"
    },
    {
      id: 2,
      name: "María Elena Vásquez",
      email: "maria.vasquez@automotriz.mx",
      role: "operator",
      status: "active",
      dailyLimit: 40,
      dailyQueriesUsed: 35,
      twoFactorEnabled: false,
      lastLogin: "2024-10-17T18:45:00Z",
      createdAt: "2024-02-20T14:30:00Z"
    },
    {
      id: 3,
      name: "José Antonio Morales",
      email: "jose.morales@seguros.mx",
      role: "auditor",
      status: "active",
      dailyLimit: 25,
      dailyQueriesUsed: 8,
      twoFactorEnabled: true,
      lastLogin: "2024-10-17T16:20:00Z",
      createdAt: "2024-03-10T09:15:00Z"
    },
    {
      id: 4,
      name: "Ana Patricia Jiménez",
      email: "ana.jimenez@concesionario.mx",
      role: "operator",
      status: "inactive",
      dailyLimit: 30,
      dailyQueriesUsed: 0,
      twoFactorEnabled: false,
      lastLogin: "2024-10-15T12:00:00Z",
      createdAt: "2024-04-05T11:45:00Z"
    },
    {
      id: 5,
      name: "Roberto Sánchez López",
      email: "roberto.sanchez@taller.mx",
      role: "operator",
      status: "blocked",
      dailyLimit: 35,
      dailyQueriesUsed: 0,
      twoFactorEnabled: false,
      lastLogin: "2024-10-10T08:30:00Z",
      createdAt: "2024-05-12T16:20:00Z"
    },
    {
      id: 6,
      name: "Lucía Fernández García",
      email: "lucia.fernandez@evaluadora.mx",
      role: "auditor",
      status: "active",
      dailyLimit: 20,
      dailyQueriesUsed: 12,
      twoFactorEnabled: true,
      lastLogin: "2024-10-17T20:10:00Z",
      createdAt: "2024-06-18T13:00:00Z"
    },
    {
      id: 7,
      name: "Miguel Ángel Torres",
      email: "miguel.torres@flotillas.mx",
      role: "operator",
      status: "active",
      dailyLimit: 45,
      dailyQueriesUsed: 42,
      twoFactorEnabled: false,
      lastLogin: "2024-10-17T17:55:00Z",
      createdAt: "2024-07-22T10:30:00Z"
    },
    {
      id: 8,
      name: "Carmen Delgado Ruiz",
      email: "carmen.delgado@peritajes.mx",
      role: "operator",
      status: "active",
      dailyLimit: 25,
      dailyQueriesUsed: 18,
      twoFactorEnabled: true,
      lastLogin: "2024-10-17T15:40:00Z",
      createdAt: "2024-08-14T12:15:00Z"
    }
  ];

  useEffect(() => {
    setUsers(mockUsers);
  }, []);

  // Filter and sort users
  const filteredAndSortedUsers = useMemo(() => {
    let filtered = users?.filter(user => {
      const matchesSearch = !filters?.search || 
        user?.name?.toLowerCase()?.includes(filters?.search?.toLowerCase()) ||
        user?.email?.toLowerCase()?.includes(filters?.search?.toLowerCase());
      const matchesRole = !filters?.role || user?.role === filters?.role;
      const matchesStatus = !filters?.status || user?.status === filters?.status;
      
      return matchesSearch && matchesRole && matchesStatus;
    });

    if (sortConfig?.key) {
      filtered?.sort((a, b) => {
        let aValue = a?.[sortConfig?.key];
        let bValue = b?.[sortConfig?.key];
        
        if (typeof aValue === 'string') {
          aValue = aValue?.toLowerCase();
          bValue = bValue?.toLowerCase();
        }
        
        if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [users, filters, sortConfig]);

  // Paginate users
  const paginatedUsers = useMemo(() => {
    const startIndex = (pagination?.currentPage - 1) * pagination?.itemsPerPage;
    return filteredAndSortedUsers?.slice(startIndex, startIndex + pagination?.itemsPerPage);
  }, [filteredAndSortedUsers, pagination]);

  // Calculate stats
  const userStats = useMemo(() => {
    const stats = {
      total: users?.length,
      active: users?.filter(u => u?.status === 'active')?.length,
      inactive: users?.filter(u => u?.status === 'inactive')?.length,
      blocked: users?.filter(u => u?.status === 'blocked')?.length
    };
    return stats;
  }, [users]);

  const totalPages = Math.ceil(filteredAndSortedUsers?.length / pagination?.itemsPerPage);

  // Event handlers
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev?.includes(userId) 
        ? prev?.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    setSelectedUsers(prev => 
      prev?.length === paginatedUsers?.length 
        ? [] 
        : paginatedUsers?.map(user => user?.id)
    );
  };

  const handleClearSelection = () => {
    setSelectedUsers([]);
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({ search: '', role: '', status: '' });
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  const handleItemsPerPageChange = (itemsPerPage) => {
    setPagination({ currentPage: 1, itemsPerPage });
  };

  const openUserModal = (user = null, mode = 'create') => {
    setModals(prev => ({
      ...prev,
      userModal: { isOpen: true, user, mode }
    }));
  };

  const closeUserModal = () => {
    setModals(prev => ({
      ...prev,
      userModal: { isOpen: false, user: null, mode: 'create' }
    }));
  };

  const openConfirmModal = (type, title, message, action) => {
    setModals(prev => ({
      ...prev,
      confirmModal: { isOpen: true, type, title, message, action }
    }));
  };

  const closeConfirmModal = () => {
    setModals(prev => ({
      ...prev,
      confirmModal: { isOpen: false, type: 'warning', title: '', message: '', action: null }
    }));
  };

  const handleSaveUser = async (userData) => {
    setIsLoading(true);
    try {
      if (modals?.userModal?.mode === 'create') {
        const newUser = {
          id: Math.max(...users?.map(u => u?.id)) + 1,
          ...userData,
          dailyQueriesUsed: 0,
          createdAt: new Date()?.toISOString(),
          lastLogin: null
        };
        setUsers(prev => [...prev, newUser]);
      } else {
        setUsers(prev => prev?.map(user => 
          user?.id === modals?.userModal?.user?.id 
            ? { ...user, ...userData }
            : user
        ));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = (user) => {
    openConfirmModal(
      'danger',
      'Eliminar Usuario',
      `¿Estás seguro de que deseas eliminar a ${user?.name}? Esta acción no se puede deshacer.`,
      () => {
        setUsers(prev => prev?.filter(u => u?.id !== user?.id));
        setSelectedUsers(prev => prev?.filter(id => id !== user?.id));
        closeConfirmModal();
      }
    );
  };

  const handleToggleStatus = (user) => {
    const newStatus = user?.status === 'blocked' ? 'active' : 'blocked';
    const action = newStatus === 'blocked' ? 'bloquear' : 'desbloquear';
    
    openConfirmModal(
      newStatus === 'blocked' ? 'warning' : 'info',
      `${action?.charAt(0)?.toUpperCase() + action?.slice(1)} Usuario`,
      `¿Deseas ${action} a ${user?.name}?`,
      () => {
        setUsers(prev => prev?.map(u => 
          u?.id === user?.id ? { ...u, status: newStatus } : u
        ));
        closeConfirmModal();
      }
    );
  };

  const handleResetPassword = (user) => {
    openConfirmModal(
      'info',
      'Restablecer Contraseña',
      `Se enviará un enlace de restablecimiento de contraseña a ${user?.email}. ¿Continuar?`,
      () => {
        // Mock password reset logic
        console.log(`Password reset sent to ${user?.email}`);
        closeConfirmModal();
      }
    );
  };

  const handleBulkAction = async (action) => {
    const selectedUserNames = users?.filter(u => selectedUsers?.includes(u?.id))?.map(u => u?.name)?.join(', ');

    const actionLabels = {
      activate: 'activar',
      deactivate: 'desactivar',
      block: 'bloquear',
      unblock: 'desbloquear',
      'reset-password': 'restablecer contraseñas de',
      'update-limits': 'actualizar límites de',
      delete: 'eliminar'
    };

    openConfirmModal(
      action === 'delete' ? 'danger' : 'warning',
      `Acción en Lote: ${actionLabels?.[action]}`,
      `¿Deseas ${actionLabels?.[action]} los siguientes usuarios?\n\n${selectedUserNames}`,
      () => {
        // Mock bulk action logic
        console.log(`Bulk action ${action} applied to users:`, selectedUsers);
        setSelectedUsers([]);
        closeConfirmModal();
      }
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Administración de Usuarios - VIN Pro Platform</title>
        <meta name="description" content="Gestión completa de usuarios y control de acceso basado en roles para profesionales automotrices" />
      </Helmet>
      <Header />
      <Sidebar 
        isCollapsed={sidebarCollapsed} 
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} 
      />
      <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-60'}`}>
        <div className="p-6">
          <Breadcrumbs />
          
          <UserStatsCards stats={userStats} />
          
          <UserFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            onAddUser={() => openUserModal()}
            totalUsers={users?.length}
            selectedCount={selectedUsers?.length}
          />

          <BulkActionsBar
            selectedCount={selectedUsers?.length}
            onBulkAction={handleBulkAction}
            onClearSelection={handleClearSelection}
          />

          <UserTable
            users={paginatedUsers}
            selectedUsers={selectedUsers}
            onSelectUser={handleSelectUser}
            onSelectAll={handleSelectAll}
            onEditUser={(user) => openUserModal(user, 'edit')}
            onDeleteUser={handleDeleteUser}
            onToggleStatus={handleToggleStatus}
            onResetPassword={handleResetPassword}
            sortConfig={sortConfig}
            onSort={handleSort}
          />

          <Pagination
            currentPage={pagination?.currentPage}
            totalPages={totalPages}
            totalItems={filteredAndSortedUsers?.length}
            itemsPerPage={pagination?.itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      </main>
      <UserModal
        isOpen={modals?.userModal?.isOpen}
        onClose={closeUserModal}
        user={modals?.userModal?.user}
        mode={modals?.userModal?.mode}
        onSave={handleSaveUser}
      />
      <ConfirmationModal
        isOpen={modals?.confirmModal?.isOpen}
        onClose={closeConfirmModal}
        onConfirm={modals?.confirmModal?.action}
        title={modals?.confirmModal?.title}
        message={modals?.confirmModal?.message}
        type={modals?.confirmModal?.type}
        isLoading={isLoading}
      />
      <QuickActionButton />
    </div>
  );
};

export default UserAdministration;